import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  RemoveCircleOutline as DeactivateIcon,
} from '@mui/icons-material';
import {
  AdminStateAgencyMapping,
  BulkImportSummary,
  stateAgencyMappingsAdminService,
  UpsertStateAgencyMappingInput,
} from '../services/stateAgencyMappingsService';

// FEAT-047 admin surface (MVP-8.19, MVP-8.35, MVP-18.41).
// Paginated table + inline create/edit dialog + CSV import + soft-delete
// ("Deactivate") action. The `verifyUrlTemplate` is editable here only;
// public web/mobile surfaces never receive the template.

interface EditDialogState {
  open: boolean;
  mode: 'create' | 'edit';
  initial: UpsertStateAgencyMappingInput | null;
}

const EMPTY_INPUT: UpsertStateAgencyMappingInput = {
  stateCode: '',
  agencyLabel: '',
  agencyFullName: '',
  registerUrl: '',
  verifyUrlTemplate: 'https://example.gov/verify?lic={licenseNumber}',
  notes: '',
  active: true,
};

const STATE_CODE_PATTERN = /^[A-Z]{2}$/;

export const StateAgencyMappingsPage: React.FC = () => {
  const [rows, setRows] = React.useState<AdminStateAgencyMapping[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [importSummary, setImportSummary] = React.useState<BulkImportSummary | null>(null);
  const [editDialog, setEditDialog] = React.useState<EditDialogState>({
    open: false,
    mode: 'create',
    initial: null,
  });
  const [draft, setDraft] = React.useState<UpsertStateAgencyMappingInput>(EMPTY_INPUT);
  const [saving, setSaving] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const loadRows = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await stateAgencyMappingsAdminService.list();
      setRows(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load state agency mappings');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const openCreate = () => {
    setDraft({ ...EMPTY_INPUT });
    setEditDialog({ open: true, mode: 'create', initial: null });
  };

  const openEdit = (row: AdminStateAgencyMapping) => {
    const initial: UpsertStateAgencyMappingInput = {
      stateCode: row.stateCode,
      agencyLabel: row.agencyLabel,
      agencyFullName: row.agencyFullName,
      registerUrl: row.registerUrl,
      verifyUrlTemplate: row.verifyUrlTemplate,
      notes: row.notes ?? '',
      active: row.active,
    };
    setDraft(initial);
    setEditDialog({ open: true, mode: 'edit', initial });
  };

  const closeDialog = () => {
    setEditDialog({ open: false, mode: 'create', initial: null });
    setSaving(false);
  };

  const handleSave = async () => {
    const code = draft.stateCode.trim().toUpperCase();
    if (!STATE_CODE_PATTERN.test(code)) {
      setError('State code must be a two-letter US state code (e.g. CA).');
      return;
    }
    if (!draft.agencyLabel.trim() || !draft.agencyFullName.trim()) {
      setError('Agency label and full name are required.');
      return;
    }
    try {
      // Pre-validate URLs so the user gets a friendly error before hitting
      // the server's structured envelope.
      // eslint-disable-next-line no-new
      new URL(draft.registerUrl);
      // eslint-disable-next-line no-new
      new URL(draft.verifyUrlTemplate);
    } catch {
      setError('Register URL and Verify URL template must be valid http(s) URLs.');
      return;
    }
    if (!draft.verifyUrlTemplate.includes('{licenseNumber}')) {
      setError('Verify URL template must include the {licenseNumber} placeholder.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload: UpsertStateAgencyMappingInput = {
        stateCode: code,
        agencyLabel: draft.agencyLabel.trim(),
        agencyFullName: draft.agencyFullName.trim(),
        registerUrl: draft.registerUrl.trim(),
        verifyUrlTemplate: draft.verifyUrlTemplate.trim(),
        notes: draft.notes?.trim() || undefined,
        active: draft.active ?? true,
      };
      await stateAgencyMappingsAdminService.upsert(payload);
      setSuccess(
        editDialog.mode === 'create'
          ? `Added state agency mapping for ${code}.`
          : `Updated state agency mapping for ${code}.`,
      );
      closeDialog();
      await loadRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save state agency mapping');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (row: AdminStateAgencyMapping) => {
    if (!window.confirm(`Deactivate ${row.stateCode}? The register and verify links will be hidden everywhere.`)) {
      return;
    }
    setError(null);
    try {
      await stateAgencyMappingsAdminService.deactivate(row.stateCode);
      setSuccess(`Deactivated ${row.stateCode}.`);
      await loadRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate state agency mapping');
    }
  };

  const handleCsvImport: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError(null);
    setImportSummary(null);
    try {
      const summary = await stateAgencyMappingsAdminService.bulkImportCsv(file);
      setImportSummary(summary);
      setSuccess(
        `Imported ${file.name}: ${summary.created} created, ${summary.updated} updated, ${summary.skipped} skipped.`,
      );
      await loadRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'CSV import failed');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const visibleRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            State Agency Mappings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Per-state licensing-agency labels and register/verify URLs. The
            verify URL template is used server-side to substitute the
            caregiver license number on the careseeker-facing profile. Public
            surfaces never receive the template directly.
          </Typography>
        </Box>

        {error ? (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {success ? (
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        ) : null}
        {importSummary && importSummary.errors.length > 0 ? (
          <Alert severity="warning" onClose={() => setImportSummary(null)}>
            <Typography variant="body2" component="div">
              {importSummary.errors.length} CSV row(s) skipped:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {importSummary.errors.map((err) => (
                <li key={`${err.line}-${err.message}`}>line {err.line}: {err.message}</li>
              ))}
            </Box>
          </Alert>
        ) : null}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            data-testid="state-agency-mappings-add"
          >
            Add mapping
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            data-testid="state-agency-mappings-csv"
          >
            Import CSV
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleCsvImport}
            aria-label="Upload state agency mappings CSV"
            data-testid="state-agency-mappings-csv-input"
          />
          <Button
            variant="text"
            startIcon={<RefreshIcon />}
            onClick={() => void loadRows()}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>

        <Paper variant="outlined">
          {loading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" aria-label="State agency mappings table">
                <TableHead>
                  <TableRow>
                    <TableCell>State</TableCell>
                    <TableCell>Label</TableCell>
                    <TableCell>Register URL</TableCell>
                    <TableCell>Verify URL template</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ py: 3, textAlign: 'center' }}
                        >
                          No state agency mappings yet. Add one or import a CSV.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleRows.map((row) => (
                      <TableRow key={row.stateCode} hover>
                        <TableCell>{row.stateCode}</TableCell>
                        <TableCell>{row.agencyLabel}</TableCell>
                        <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.registerUrl}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.verifyUrlTemplate}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 240 }}>{row.notes ?? '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={row.active ? 'success' : 'default'}
                            label={row.active ? 'Active' : 'Inactive'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(row.updatedAt).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => openEdit(row)}
                              aria-label={`Edit state agency mapping for ${row.stateCode}`}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deactivate">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => void handleDeactivate(row)}
                                disabled={!row.active}
                                aria-label={`Deactivate state agency mapping for ${row.stateCode}`}
                              >
                                <DeactivateIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </TableContainer>
          )}
        </Paper>
      </Stack>

      <Dialog open={editDialog.open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {editDialog.mode === 'create'
            ? 'Add state agency mapping'
            : `Edit ${editDialog.initial?.stateCode ?? ''}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="State code (2 letters)"
              value={draft.stateCode}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, stateCode: event.target.value.toUpperCase() }))
              }
              disabled={editDialog.mode === 'edit'}
              inputProps={{ maxLength: 2, 'data-testid': 'state-agency-mappings-state' }}
              fullWidth
            />
            <TextField
              label="Agency label (short, e.g. HCSB)"
              value={draft.agencyLabel}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, agencyLabel: event.target.value }))
              }
              inputProps={{ maxLength: 120, 'data-testid': 'state-agency-mappings-label' }}
              fullWidth
            />
            <TextField
              label="Agency full name"
              value={draft.agencyFullName}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, agencyFullName: event.target.value }))
              }
              inputProps={{ maxLength: 240, 'data-testid': 'state-agency-mappings-fullname' }}
              fullWidth
            />
            <TextField
              label="Register URL"
              value={draft.registerUrl}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, registerUrl: event.target.value }))
              }
              inputProps={{ 'data-testid': 'state-agency-mappings-register-url' }}
              fullWidth
              helperText="Where caregivers go to register. Opens in a new tab from the caregiver settings."
            />
            <TextField
              label="Verify URL template (must include {licenseNumber})"
              value={draft.verifyUrlTemplate}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, verifyUrlTemplate: event.target.value }))
              }
              inputProps={{ 'data-testid': 'state-agency-mappings-verify-template' }}
              fullWidth
              helperText="Server substitutes the caregiver license number before the careseeker-facing link renders."
            />
            <TextField
              label="Notes (admin-only)"
              value={draft.notes ?? ''}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, notes: event.target.value }))
              }
              inputProps={{ maxLength: 2000 }}
              multiline
              minRows={2}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={draft.active ?? true}
                  onChange={(event) =>
                    setDraft((prev) => ({ ...prev, active: event.target.checked }))
                  }
                  inputProps={{ 'aria-label': 'Mapping active toggle' }}
                />
              }
              label={draft.active === false ? 'Inactive (hidden from caregiver + careseeker surfaces)' : 'Active'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving}
            data-testid="state-agency-mappings-save"
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StateAgencyMappingsPage;
